import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { 
    Box, Typography, Paper, IconButton, Button, Tooltip, Chip, LinearProgress
} from '@mui/material';
import { 
    PlayArrow as PlayIcon, 
    Pause as PauseIcon, 
    Refresh as ResetIcon, 
    Close as CloseIcon, 
    DragIndicator as DragIcon,
    VolumeUp as VolumeIcon,
    OpenInFull as ExpandIcon,
    CloseFullscreen as MinimizeIcon,
    Alarm as AlarmIcon,
    Add as AddIcon,
    Palette as PaletteIcon,
    Check as CheckIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { closeTimer } from '../../redux/slices/timerSlice';

interface FloatingCountdownTimerProps {
    open?: boolean;
    onClose?: () => void;
}

type SizeMode = 'mini' | 'normal' | 'large';

interface TimerTheme {
    id: string;
    name: string;
    icon: string;
    primary: string;
    paperBg: string;
    headerBg: string;
    textColor: string;
    subTextColor: string;
    numberColor: string;
    barBg: string;
    borderColor: string;
    shadow: string;
    isDark: boolean;
    glow?: string;
}

const TIMER_THEMES: TimerTheme[] = [
    {
        id: 'indigo',
        name: 'Indigo Hiện đại',
        icon: '🔵',
        primary: '#6366f1',
        paperBg: '#ffffff',
        headerBg: '#f8fafc',
        textColor: '#1e293b',
        subTextColor: '#64748b',
        numberColor: '#1e293b',
        barBg: '#e2e8f0',
        borderColor: '#cbd5e1',
        shadow: '0 12px 36px rgba(15, 23, 42, 0.16)',
        isDark: false
    },
    {
        id: 'emerald',
        name: 'Emerald Tươi mát',
        icon: '🟢',
        primary: '#10b981',
        paperBg: '#ffffff',
        headerBg: '#f0fdf4',
        textColor: '#064e3b',
        subTextColor: '#047857',
        numberColor: '#065f46',
        barBg: '#dcfce7',
        borderColor: '#86efac',
        shadow: '0 12px 36px rgba(5, 150, 105, 0.18)',
        isDark: false
    },
    {
        id: 'amber',
        name: 'Amber Năng động',
        icon: '🟡',
        primary: '#f59e0b',
        paperBg: '#ffffff',
        headerBg: '#fffbeb',
        textColor: '#78350f',
        subTextColor: '#b45309',
        numberColor: '#92400e',
        barBg: '#fef3c7',
        borderColor: '#fde68a',
        shadow: '0 12px 36px rgba(217, 119, 6, 0.18)',
        isDark: false
    },
    {
        id: 'rose',
        name: 'Rose Nhiệt huyết',
        icon: '🔴',
        primary: '#f43f5e',
        paperBg: '#ffffff',
        headerBg: '#fff1f2',
        textColor: '#881337',
        subTextColor: '#be123c',
        numberColor: '#9f1239',
        barBg: '#ffe4e6',
        borderColor: '#fecdd3',
        shadow: '0 12px 36px rgba(244, 63, 94, 0.18)',
        isDark: false
    },
    {
        id: 'dark',
        name: 'Dark OLED',
        icon: '⚫',
        primary: '#38bdf8',
        paperBg: '#0f172a',
        headerBg: '#1e293b',
        textColor: '#f8fafc',
        subTextColor: '#94a3b8',
        numberColor: '#38bdf8',
        barBg: '#334155',
        borderColor: '#334155',
        shadow: '0 16px 40px rgba(0, 0, 0, 0.6)',
        isDark: true,
        glow: '0 0 16px rgba(56, 189, 248, 0.5)'
    },
    {
        id: 'cyberpunk',
        name: 'Neon Game Show',
        icon: '⚡',
        primary: '#a855f7',
        paperBg: '#18181b',
        headerBg: '#27272a',
        textColor: '#fafafa',
        subTextColor: '#a1a1aa',
        numberColor: '#4ade80',
        barBg: '#27272a',
        borderColor: '#3f3f46',
        shadow: '0 16px 40px rgba(168, 85, 247, 0.25)',
        isDark: true,
        glow: '0 0 20px rgba(74, 222, 128, 0.6)'
    }
];

const FONT_STYLES = [
    { id: 'monospace', label: '📟 Điện tử', font: '"Courier New", Courier, monospace' },
    { id: 'sans', label: '🎯 Tối giản', font: 'system-ui, -apple-system, sans-serif' },
    { id: 'impact', label: '🎮 Arcade', font: '"Impact", "Arial Black", sans-serif' }
];

// School bell synthesizer using Web Audio API
const playSchoolBell = () => {
    try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) return;
        const ctx = new AudioContextClass();

        // Westminster chime 4-tone sequence: E5, C5, D5, G4
        const chimeNotes = [659.25, 523.25, 587.33, 392.00];
        chimeNotes.forEach((freq, index) => {
            const startTime = ctx.currentTime + index * 0.42;
            
            // Fundamental chime tone
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, startTime);
            gain.gain.setValueAtTime(0.4, startTime);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + 1.2);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(startTime);
            osc.stop(startTime + 1.2);

            // Harmonic bell resonance
            const harmonic = ctx.createOscillator();
            const hGain = ctx.createGain();
            harmonic.type = 'triangle';
            harmonic.frequency.setValueAtTime(freq * 2.76, startTime);
            hGain.gain.setValueAtTime(0.15, startTime);
            hGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.8);
            harmonic.connect(hGain);
            hGain.connect(ctx.destination);
            harmonic.start(startTime);
            harmonic.stop(startTime + 0.8);
        });
    } catch (e) {
        console.warn('Web Audio error:', e);
    }
};

const announceTimeUp = () => {
    try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const url = `${baseURL}/utils/tts?text=${encodeURIComponent('Đã hết thời gian!')}&lang=vi`;
        const audio = new Audio(url);
        audio.play().catch(e => console.warn('TTS error:', e));
    } catch (e) {
        console.warn(e);
    }
};

const PRESETS = [
    { label: '30s', seconds: 30 },
    { label: '1p', seconds: 60 },
    { label: '2p', seconds: 120 },
    { label: '3p', seconds: 180 },
    { label: '5p', seconds: 300 },
    { label: '10p', seconds: 600 },
    { label: '15p', seconds: 900 }
];

const FloatingCountdownTimer: React.FC<FloatingCountdownTimerProps> = ({ open, onClose }) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const reduxOpen = useSelector((state: RootState) => state.timer?.isOpen);
    const isVisible = open !== undefined ? open : Boolean(reduxOpen);

    const handleClose = () => {
        if (onClose) onClose();
        dispatch(closeTimer());
    };

    const [sizeMode, setSizeMode] = useState<SizeMode>('normal');
    const [totalSeconds, setTotalSeconds] = useState<number>(300); // 5 mins default
    const [timeLeft, setTimeLeft] = useState<number>(300);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [isAlarming, setIsAlarming] = useState<boolean>(false);

    // Theme & Styling Options
    const [themeId, setThemeId] = useState<string>(() => {
        try {
            return localStorage.getItem('countdown_timer_theme') || 'indigo';
        } catch {
            return 'indigo';
        }
    });
    const [fontId, setFontId] = useState<string>(() => {
        try {
            return localStorage.getItem('countdown_timer_font') || 'monospace';
        } catch {
            return 'monospace';
        }
    });
    const [settingsOpen, setSettingsOpen] = useState(false);

    // Freeform resize state
    const [customWidth, setCustomWidth] = useState<number>(330);
    const [customHeight, setCustomHeight] = useState<number>(270);

    const theme = TIMER_THEMES.find(t => t.id === themeId) || TIMER_THEMES[0];
    const fontObj = FONT_STYLES.find(f => f.id === fontId) || FONT_STYLES[0];

    const handleChangeTheme = (newId: string) => {
        setThemeId(newId);
        try {
            localStorage.setItem('countdown_timer_theme', newId);
        } catch (e) {
            console.warn(e);
        }
    };

    const handleChangeFont = (newId: string) => {
        setFontId(newId);
        try {
            localStorage.setItem('countdown_timer_font', newId);
        } catch (e) {
            console.warn(e);
        }
    };

    // Timer interval
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        if (isRunning && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        setIsRunning(false);
                        setIsAlarming(true);
                        playSchoolBell();
                        announceTimeUp();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isRunning, timeLeft]);

    // Handle corner drag resize
    const handleMouseDownResize = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startW = customWidth;
        const startH = customHeight;

        const onMouseMove = (moveEvent: MouseEvent) => {
            const newW = Math.max(220, Math.min(650, startW + (moveEvent.clientX - startX)));
            const newH = Math.max(140, Math.min(500, startH + (moveEvent.clientY - startY)));
            setCustomWidth(newW);
            setCustomHeight(newH);
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    if (!isVisible) return null;

    const handleSelectPreset = (sec: number) => {
        setIsRunning(false);
        setIsAlarming(false);
        setTotalSeconds(sec);
        setTimeLeft(sec);
    };

    const handleAddSeconds = (sec: number) => {
        setTimeLeft((prev) => prev + sec);
        setTotalSeconds((prev) => Math.max(prev, timeLeft + sec));
        setIsAlarming(false);
    };

    const handleReset = () => {
        setIsRunning(false);
        setIsAlarming(false);
        setTimeLeft(totalSeconds);
    };

    const togglePlay = () => {
        if (timeLeft === 0) {
            setTimeLeft(totalSeconds);
            setIsAlarming(false);
            setIsRunning(true);
        } else {
            setIsRunning((prev) => !prev);
        }
    };

    const handleStopAlarm = () => {
        setIsAlarming(false);
        setTimeLeft(totalSeconds);
    };

    // Format mm:ss or hh:mm:ss
    const formatTime = (secs: number) => {
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        const s = secs % 60;
        if (h > 0) {
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const progress = totalSeconds > 0 ? (timeLeft / totalSeconds) * 100 : 0;
    const isEndingSoon = timeLeft <= 10 && timeLeft > 0;

    // Initial position on screen
    const defaultX = typeof window !== 'undefined' ? Math.max(20, window.innerWidth - 380) : 100;
    const defaultY = 90;

    // Calculate dimensions based on mode
    const currentWidth = sizeMode === 'mini' ? 240 : sizeMode === 'large' ? 460 : customWidth;
    const currentHeight = sizeMode === 'mini' ? 'auto' : sizeMode === 'large' ? 360 : customHeight;

    return createPortal(
        <Draggable 
            handle=".timer-drag-handle" 
            nodeRef={nodeRef} 
            bounds="body"
            defaultPosition={{ x: defaultX, y: defaultY }}
        >
            <div 
                ref={nodeRef} 
                style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    zIndex: 9999,
                    userSelect: 'none'
                }}
            >
                <Paper
                    elevation={12}
                    sx={{
                        width: currentWidth,
                        minHeight: currentHeight,
                        borderRadius: 3.5,
                        overflow: 'hidden',
                        bgcolor: isAlarming ? (theme.isDark ? '#4c0519' : '#fff1f2') : theme.paperBg,
                        border: isAlarming 
                            ? '3px solid #f43f5e' 
                            : isEndingSoon 
                            ? '2px solid #f59e0b' 
                            : `1px solid ${theme.borderColor}`,
                        boxShadow: isAlarming 
                            ? '0 0 30px rgba(244, 63, 94, 0.5)' 
                            : theme.shadow,
                        animation: isAlarming ? 'alarmGlow 0.8s infinite alternate' : 'none',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'border-color 0.3s, background-color 0.3s'
                    }}
                >
                    {/* Header Bar */}
                    <Box 
                        className="timer-drag-handle"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 0.8,
                            bgcolor: isAlarming ? (theme.isDark ? '#881337' : '#ffe4e6') : theme.headerBg,
                            borderBottom: `1px solid ${theme.borderColor}`,
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <DragIcon sx={{ fontSize: 18, color: theme.subTextColor }} />
                            <AlarmIcon sx={{ fontSize: 18, color: isAlarming ? '#e11d48' : theme.primary }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: theme.textColor, fontSize: '0.8rem', letterSpacing: 0.5 }}>
                                {sizeMode === 'mini' ? 'ĐỒNG HỒ' : 'ĐỒNG HỒ ĐẾM NGƯỢC'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                            {/* Theme Customization Toggle */}
                            <Tooltip title="Đổi màu sắc & giao diện">
                                <IconButton 
                                    size="small" 
                                    onClick={() => setSettingsOpen((prev) => !prev)}
                                    sx={{ 
                                        p: 0.4, 
                                        color: settingsOpen ? theme.primary : theme.subTextColor,
                                        bgcolor: settingsOpen ? (theme.isDark ? '#334155' : '#e2e8f0') : 'transparent' 
                                    }}
                                >
                                    <PaletteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>

                            {/* Size Toggles */}
                            <Tooltip title={sizeMode === 'mini' ? 'Mở rộng' : 'Thu nhỏ'}>
                                <IconButton 
                                    size="small" 
                                    onClick={() => setSizeMode(sizeMode === 'mini' ? 'normal' : 'mini')}
                                    sx={{ p: 0.4, color: theme.subTextColor }}
                                >
                                    {sizeMode === 'mini' ? <ExpandIcon sx={{ fontSize: 16 }} /> : <MinimizeIcon sx={{ fontSize: 16 }} />}
                                </IconButton>
                            </Tooltip>
                            {sizeMode !== 'mini' && (
                                <Tooltip title={sizeMode === 'large' ? 'Cỡ vừa' : 'Cỡ to cho cả lớp'}>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setSizeMode(sizeMode === 'large' ? 'normal' : 'large')}
                                        sx={{ p: 0.4, color: theme.subTextColor }}
                                    >
                                        <ExpandIcon sx={{ fontSize: 16, transform: sizeMode === 'large' ? 'rotate(180deg)' : 'none' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Đóng đồng hồ">
                                <IconButton size="small" onClick={handleClose} sx={{ p: 0.4, ml: 0.5, color: theme.subTextColor }}>
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* SETTINGS DRAWER / PANEL */}
                    {settingsOpen && (
                        <Box sx={{ 
                            p: 1.5, 
                            bgcolor: theme.isDark ? '#1e293b' : '#f8fafc', 
                            borderBottom: `1px solid ${theme.borderColor}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.2,
                            animation: 'fadeIn 0.2s ease'
                        }}>
                            {/* Color Themes */}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: theme.subTextColor, display: 'block', mb: 0.8, fontSize: '0.72rem' }}>
                                    🎨 BẢNG MÀU GIAO DIỆN
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {TIMER_THEMES.map((t) => (
                                        <Tooltip key={t.id} title={t.name}>
                                            <Box
                                                onClick={() => handleChangeTheme(t.id)}
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: '50%',
                                                    bgcolor: t.primary,
                                                    border: themeId === t.id ? '2px solid #fff' : '2px solid transparent',
                                                    outline: themeId === t.id ? `2px solid ${t.primary}` : 'none',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#fff',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'scale(1.15)' }
                                                }}
                                            >
                                                {themeId === t.id && <CheckIcon sx={{ fontSize: 16 }} />}
                                            </Box>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>

                            {/* Font Styles */}
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: theme.subTextColor, display: 'block', mb: 0.8, fontSize: '0.72rem' }}>
                                    🔤 KIỂU CHỮ SỐ
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                                    {FONT_STYLES.map((f) => (
                                        <Chip
                                            key={f.id}
                                            label={f.label}
                                            size="small"
                                            clickable
                                            onClick={() => handleChangeFont(f.id)}
                                            variant={fontId === f.id ? 'filled' : 'outlined'}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '0.72rem',
                                                bgcolor: fontId === f.id ? theme.primary : 'transparent',
                                                color: fontId === f.id ? '#fff' : theme.textColor,
                                                borderColor: theme.borderColor,
                                                '&:hover': { bgcolor: fontId === f.id ? theme.primary : (theme.isDark ? '#334155' : '#e2e8f0') }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* MINI MODE VIEW */}
                    {sizeMode === 'mini' ? (
                        <Box sx={{ p: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ 
                                fontFamily: fontObj.font, 
                                fontSize: '1.4rem', 
                                fontWeight: 900, 
                                color: isAlarming ? '#e11d48' : isEndingSoon ? '#d97706' : theme.numberColor,
                                letterSpacing: 1,
                                textShadow: theme.glow || 'none'
                            }}>
                                {formatTime(timeLeft)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton 
                                    size="small" 
                                    onClick={togglePlay}
                                    sx={{ bgcolor: isRunning ? (theme.isDark ? '#334155' : '#f1f5f9') : theme.primary, color: isRunning ? theme.textColor : '#fff', '&:hover': { opacity: 0.9 } }}
                                >
                                    {isRunning ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                </IconButton>
                                <IconButton size="small" onClick={handleReset} sx={{ bgcolor: theme.isDark ? '#334155' : '#f1f5f9', color: theme.textColor }}>
                                    <ResetIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ) : (
                        /* NORMAL & LARGE MODE VIEW */
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between' }}>
                            {/* Alarming Banner */}
                            {isAlarming && (
                                <Box sx={{ 
                                    bgcolor: '#e11d48', 
                                    color: '#fff', 
                                    borderRadius: 2, 
                                    py: 0.6, 
                                    px: 1.5, 
                                    mb: 1.5, 
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}>
                                    <VolumeIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 900, letterSpacing: 0.5 }}>
                                        ⏰ ĐÃ HẾT GIỜ LÀM BÀI!
                                    </Typography>
                                    <Button 
                                        size="small" 
                                        variant="contained" 
                                        onClick={handleStopAlarm}
                                        sx={{ 
                                            bgcolor: '#fff', 
                                            color: '#e11d48', 
                                            fontWeight: 800, 
                                            fontSize: '0.72rem',
                                            py: 0.2, 
                                            px: 1, 
                                            minWidth: 'auto',
                                            '&:hover': { bgcolor: '#ffe4e6' }
                                        }}
                                    >
                                        Tắt chuông
                                    </Button>
                                </Box>
                            )}

                            {/* Digital Clock Display */}
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                my: 'auto',
                                py: sizeMode === 'large' ? 2 : 1 
                            }}>
                                <Typography sx={{ 
                                    fontFamily: fontObj.font, 
                                    fontSize: sizeMode === 'large' ? '4.2rem' : '2.8rem', 
                                    fontWeight: 900, 
                                    color: isAlarming ? '#e11d48' : isEndingSoon ? '#d97706' : theme.numberColor,
                                    letterSpacing: 2,
                                    lineHeight: 1,
                                    textShadow: theme.glow || 'none'
                                }}>
                                    {formatTime(timeLeft)}
                                </Typography>

                                {/* Progress Bar */}
                                <Box sx={{ width: '90%', mt: 1.5 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={progress} 
                                        sx={{ 
                                            height: 8, 
                                            borderRadius: 4,
                                            bgcolor: theme.barBg,
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 4,
                                                bgcolor: isAlarming ? '#e11d48' : isEndingSoon ? '#f59e0b' : theme.primary,
                                                transition: 'transform 0.4s ease'
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Preset Buttons */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, justifyContent: 'center', mb: 1.5 }}>
                                {PRESETS.map((p) => (
                                    <Chip 
                                        key={p.label}
                                        label={p.label}
                                        size="small"
                                        clickable
                                        onClick={() => handleSelectPreset(p.seconds)}
                                        variant={totalSeconds === p.seconds && !isRunning ? 'filled' : 'outlined'}
                                        sx={{ 
                                            fontWeight: 700, 
                                            fontSize: '0.72rem',
                                            bgcolor: totalSeconds === p.seconds && !isRunning ? theme.primary : 'transparent',
                                            color: totalSeconds === p.seconds && !isRunning ? '#fff' : theme.textColor,
                                            borderColor: theme.borderColor,
                                            '&:hover': { bgcolor: totalSeconds === p.seconds && !isRunning ? theme.primary : (theme.isDark ? '#334155' : '#f1f5f9') }
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Main Action Buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAddSeconds(30)}
                                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                    sx={{ 
                                        borderRadius: 2, 
                                        textTransform: 'none', 
                                        fontWeight: 700, 
                                        fontSize: '0.75rem',
                                        color: theme.textColor,
                                        borderColor: theme.borderColor
                                    }}
                                >
                                    +30s
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAddSeconds(60)}
                                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                    sx={{ 
                                        borderRadius: 2, 
                                        textTransform: 'none', 
                                        fontWeight: 700, 
                                        fontSize: '0.75rem',
                                        color: theme.textColor,
                                        borderColor: theme.borderColor
                                    }}
                                >
                                    +1p
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={togglePlay}
                                    startIcon={isRunning ? <PauseIcon /> : <PlayIcon />}
                                    sx={{
                                        borderRadius: 3,
                                        px: 2.5,
                                        py: 0.8,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                        fontSize: '0.9rem',
                                        bgcolor: isRunning ? '#f59e0b' : theme.primary,
                                        boxShadow: `0 4px 12px ${theme.primary}40`,
                                        color: '#fff',
                                        '&:hover': { bgcolor: isRunning ? '#d97706' : theme.primary, opacity: 0.95 }
                                    }}
                                >
                                    {isRunning ? 'Tạm dừng' : 'Bắt đầu'}
                                </Button>
                                <IconButton 
                                    onClick={handleReset}
                                    sx={{ bgcolor: theme.isDark ? '#334155' : '#f1f5f9', color: theme.textColor, p: 1, '&:hover': { bgcolor: theme.isDark ? '#475569' : '#e2e8f0' } }}
                                >
                                    <ResetIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    )}

                    {/* Corner Drag Resize Handle */}
                    {sizeMode === 'normal' && (
                        <Box
                            onMouseDown={handleMouseDownResize}
                            sx={{
                                position: 'absolute',
                                right: 2,
                                bottom: 2,
                                width: 16,
                                height: 16,
                                cursor: 'nwse-resize',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.subTextColor,
                                '&::after': {
                                    content: '""',
                                    width: 6,
                                    height: 6,
                                    borderRight: `2px solid ${theme.subTextColor}`,
                                    borderBottom: `2px solid ${theme.subTextColor}`
                                }
                            }}
                        />
                    )}
                </Paper>

                {/* Animation keyframes */}
                <style>{`
                    @keyframes alarmGlow {
                        0% { box-shadow: 0 0 15px rgba(244, 63, 94, 0.4); }
                        100% { box-shadow: 0 0 35px rgba(244, 63, 94, 0.8); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(-6px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        </Draggable>,
        document.body
    );
};

export default FloatingCountdownTimer;
